/**
 * Este archivo proporciona un gestor de eventos global que está disponible
 * sin depender de React. Se carga en main.jsx para estar disponible
 * desde el inicio de la aplicación.
 */

import Logger from '../Services/loggerService';

Logger.system('Inicializando sistema de eventos globales');

// Inicializar como un IIFE para evitar contaminar el scope global
(function() {
    Logger.globalEvent('Inicializando...');
    
    // Variables globales para almacenar información del usuario
    let currentNit = null;
    let currentProfileImage = null;
    
    // Contador de intentos por NIT para evitar bucles infinitos
    const retryCounter = {};
    const MAX_RETRIES = 5;
    
    // Función para obtener el NIT actual
    const getCurrentNit = () => {
        try {
            // Usar el NIT almacenado si ya lo tenemos
            if (currentNit) return currentNit;
            
            // Intentar obtener NIT de sessionStorage primero (más rápido)
            const sessionNit = sessionStorage.getItem('nit');
            if (sessionNit) {
                currentNit = sessionNit;
                return sessionNit;
            }
            
            // Intentar obtener NIT de cookies como fallback
            const cookies = document.cookie.split('; ');
            const nitCookie = cookies.find(row => row.startsWith('nit='));
            if (nitCookie) {
                currentNit = decodeURIComponent(nitCookie.split('=')[1]);
                return currentNit;
            }
            
            return null;
        } catch (e) {
            Logger.error('Error obteniendo NIT:', e);
            return null;
        }
    };
    
    // Listener para eventos de login que actualiza la información del usuario
    const handleLoginEvent = (event) => {
        
        if (event?.detail?.nit) {
            // Actualizar NIT
            currentNit = event.detail.nit;
            
            // Intentar guardar en sessionStorage para persistencia
            try {
                sessionStorage.setItem('nit', currentNit);
            } catch (e) {}
            
            // Si el evento contiene información de imagen, actualizarla directamente
            if (event.detail.hasProfileImage && event.detail.profileImage) {
                currentProfileImage = event.detail.profileImage;
                
                // Guardar en localStorage
                try {
                    localStorage.setItem(`profileImage_${currentNit}`, currentProfileImage);
                    
                    // Notificar a la aplicación
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
                            detail: {
                                imageUrl: currentProfileImage,
                                timestamp: Date.now(),
                                fromCache: false,
                                userNit: currentNit
                            }
                        }));
                    }, 100);
                } catch (e) {
                    Logger.error('Error guardando imagen:', e);
                }
            }
            
            // Resetear contador de reintentos
            retryCounter[currentNit] = 0;
        }
    };
    
    // Registrar listeners para eventos de login
    window.addEventListener('loginSuccess', handleLoginEvent);
    window.addEventListener('userAuthenticated', handleLoginEvent);
    
    // Intentar recuperar imagen de perfil de localStorage al inicio
    const checkInitialProfileImage = () => {
        try {
            const nit = getCurrentNit();
            if (!nit) {
                return;
            }
            
            // Si ya tenemos imagen en memoria, usarla directamente
            if (currentProfileImage) {
                window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
                    detail: {
                        imageUrl: currentProfileImage,
                        timestamp: Date.now(),
                        fromCache: true,
                        userNit: nit
                    }
                }));
                return;
            }
            
            // Intentar obtener imagen de localStorage
            const cacheKey = `profileImage_${nit}`;
            const cachedImage = localStorage.getItem(cacheKey);
            
            if (cachedImage) {
                Logger.globalEvent('Imagen de perfil encontrada en caché');
                currentProfileImage = cachedImage; // Actualizar en memoria
                
                // Notificar a los componentes sobre la imagen en caché
                window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
                    detail: {
                        imageUrl: cachedImage,
                        timestamp: Date.now(),
                        fromCache: true,
                        userNit: nit
                    }
                }));
            } else {
                Logger.globalEvent('No se encontró imagen en caché (la cargará el ProfilePictureContext)', { nit });
                // Ya no disparamos profilePictureRefreshNeeded ni reintentamos aquí.
                // ProfilePictureContext se encargará de la carga inicial y la persistencia.
            }
        } catch (error) {
            Logger.error('Error verificando caché inicial:', error);
        }
    };
    
    // Ejecutar verificación inicial después de un breve retraso
    setTimeout(checkInitialProfileImage, 100);
    
    // Escuchar eventos de login
    const listenForAuthEvents = () => {
        window.addEventListener('loginSuccess', (event) => {
            // Restablecer contador de intentos
            const nit = event?.detail?.nit || getCurrentNit();
            if (nit) {
                retryCounter[nit] = 0;
                currentNit = nit;
            }
            
            // Verificar inmediatamente y luego programar otra verificación
            setTimeout(checkInitialProfileImage, 50);
            setTimeout(checkInitialProfileImage, 500);
        });
        
        window.addEventListener('userAuthenticated', (event) => {
            setTimeout(checkInitialProfileImage, 100);
        });
        
        // También escuchar cuando se actualiza la imagen de perfil
        window.addEventListener('profilePictureUpdated', (event) => {
            const nit = event?.detail?.userNit || getCurrentNit();
            const imageUrl = event?.detail?.imageUrl;
            
            if (nit && imageUrl) {
                // Actualizar en memoria
                if (!event.detail.fromCache) {
                    currentProfileImage = imageUrl;
                    Logger.globalEvent('Actualizando caché con nueva imagen de perfil');
                    try {
                        localStorage.setItem(`profileImage_${nit}`, imageUrl);
                    } catch (e) {
                        Logger.error('Error guardando imagen en caché:', e);
                    }
                } else if (!currentProfileImage) {
                    // Si viene de caché pero no tenemos imagen en memoria, actualizarla
                    currentProfileImage = imageUrl;
                }
            }
        });
    };
    
    listenForAuthEvents();
    
    Logger.globalEvent('Inicializado correctamente');
})();

Logger.system('Sistema de eventos globales inicializado correctamente');
